import { SignupState as RegistrationValues } from '@/types/Signup';
import { CACHE_TIME } from '@/const/cache';
import { HTTP } from '@/const/http';
import { CreateInvoiceResponse } from '@/app/api/stripe/invoices/create/route';
import Stripe from 'stripe';

/**
 * Card declines (`cause: 'Stripe'`) are expected, user-facing outcomes that
 * the forms already display. RUM forwards console.error as errors, so log
 * them as warnings to keep them out of Error Tracking.
 */
export const isStripeDecline = (error: unknown): boolean =>
  error instanceof Error && error.cause === 'Stripe';

const logStripeClientError = (label: string, error: unknown) =>
  isStripeDecline(error)
    ? console.warn(label, error)
    : console.error(label, error);

type CancelTransactionParams = {
  paymentIntentId?: string;
  invoiceId?: string;
  reason?: 'requested_by_customer' | 'duplicate' | 'fraudulent';
};

/**
 * Creates new customer and attaches a payment method (card)
 * @param user
 * @param stripeCouponId - Optional Stripe coupon ID to apply to the customer
 * @returns
 */
export async function attachPaymentMethodToNewCustomer(
  user: RegistrationValues,
  stripeCouponId?: string
): Promise<any> {
  try {
    const response = await fetch('/api/stripe/attach/payment_method', {
      method: 'POST',
      body: JSON.stringify({
        paymentMethodTokenId: user.stripePaymentMethodTokenId,
        user,
        isNewCustomer: true,
        setNewMethodAsDefault: true,
        stripeCouponId
      }),
      next: {
        revalidate: CACHE_TIME.NO_CACHE
      }
    });

    const { body } = await response.json();

    if (response.status !== HTTP.OK) {
      if (body?.raw?.message) {
        throw Error(body.raw.message, { cause: 'Stripe' });
      }
    }

    return body;
  } catch (error) {
    logStripeClientError('[attachPaymentMethod()]', error);
    throw error;
  }
}

/**
 * Attaches a payment method (card) to a customer
 * @param paymentMethodTokenId
 * @param customerId
 * @returns {Promise<Stripe.PaymentMethod>} The newly attached payment method
 */
export async function attachPaymentMethod(
  paymentMethodTokenId: string,
  customerId: string,
  isNewCustomer: boolean = false,
  setNewMethodAsDefault: boolean = false
): Promise<Stripe.PaymentMethod> {
  try {
    const response = await fetch('/api/stripe/attach/payment_method', {
      method: 'POST',
      body: JSON.stringify({
        paymentMethodTokenId,
        customerId,
        isNewCustomer,
        setNewMethodAsDefault
      }),
      next: {
        revalidate: CACHE_TIME.NO_CACHE
      }
    });

    const { body } = await response.json();
    const { newPaymentMethod } = body;

    if (response.status !== HTTP.OK) {
      if (body?.raw?.message) {
        throw Error(body.raw.message, { cause: 'Stripe' });
      }
    }

    return newPaymentMethod;
  } catch (error) {
    logStripeClientError('[attachPaymentMethod()]', error);
    throw error;
  }
}

/**
 * Creates a new invoice with the given line items
 * @param {string} stripeCustomerId - The Stripe customer ID
 * @param {Array<{description: string; amount: number; quantity: number}>} lineItems - Array of line items to add to the invoice
 * @param {CreateInvoiceRequest} options - Optional parameters for invoice creation
 * @returns {Promise<CreateInvoiceResponse>} The created invoice
 */
export async function createInvoice(
  stripeCustomerId: string,
  lineItems?: { description: string; amount: number; quantity: number }[],
  description?: string
): Promise<CreateInvoiceResponse> {
  try {
    const response = await fetch('/api/stripe/invoices/create', {
      method: 'POST',
      body: JSON.stringify({ lineItems, stripeCustomerId, description }),
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create invoice', {
        cause: 'Stripe'
      });
    }

    return data;
  } catch (error) {
    console.error('[createInvoice()]', error);
    throw error;
  }
}

/**
 * Pays an existing invoice using the customer's default payment method
 * @param {string} invoiceId - The ID of the invoice to pay
 * @param {string} stripeCustomerId - The Stripe customer ID
 * @param {PayInvoiceRequest} options - Optional parameters for invoice payment
 * @returns {Promise<Stripe.Invoice>} The paid invoice
 */
export async function payInvoice(
  invoiceId: string,
  stripeCustomerId: string,
  lineItems?: { description: string; cartItemId: string; quantity: number }[],
  nonDefaultPaymentMethodIdToUse?: string,
  cartId?: string
): Promise<Stripe.Invoice> {
  try {
    const response = await fetch('/api/stripe/invoices/pay', {
      method: 'POST',
      body: JSON.stringify({
        invoiceId,
        stripeCustomerId,
        nonDefaultPaymentMethodIdToUse,
        cartId,
        lineItems
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    return data.invoice;
  } catch (error) {
    console.error('[payInvoice()]', error);
    throw error;
  }
}

/**
 * Creates a new invoice and pays it in a single operation
 * @param {string} stripeCustomerId - The Stripe customer ID
 * @param {Array<{description: string; amount: number; quantity: number}>} lineItems - Array of line items to add to the invoice
 * @returns {Promise<Stripe.Invoice>} The created and paid invoice
 */
export async function createAndPayInvoice(
  stripeCustomerId: string,
  lineItems: { description: string; amount: number; quantity: number }[]
): Promise<Stripe.Invoice> {
  try {
    // First create the invoice
    const invoice = await createInvoice(stripeCustomerId, lineItems);

    // Then pay the invoice
    return await payInvoice(invoice.invoice.id, stripeCustomerId);
  } catch (error) {
    console.error('[createAndPayInvoice()]', error);
    throw error;
  }
}

/**
 * Creates new Tax Calculation for associated lineItems
 * @param {string} stripeCustomerId
 * @param {{description: string; amount: number; quantity: number}[]} lineItems
 * @returns {Promise<Stripe.Tax.Calculation>} Promise that resolves to the response from the Stripe API call
 */

/**
 * Cancels a transaction (payment intent or invoice)
 * @param {CancelTransactionParams} params - Object containing either paymentIntentId or invoiceId, and an optional reason
 * @returns {Promise<{cancelled: boolean; type: string; status: string}>} - Result of the cancellation
 */
export async function cancelTransaction(
  params: CancelTransactionParams
): Promise<{ cancelled: boolean; type: string; status: string }> {
  try {
    const { paymentIntentId, invoiceId, reason } = params;

    if (!paymentIntentId && !invoiceId) {
      throw new Error('Either paymentIntentId or invoiceId is required');
    }

    const response = await fetch('/api/stripe/cancel', {
      method: 'POST',
      body: JSON.stringify({ paymentIntentId, invoiceId, reason }),
      next: {
        revalidate: CACHE_TIME.NO_CACHE
      }
    });

    const { body } = await response.json();

    if (response.status !== HTTP.OK) {
      throw new Error(body?.message || 'Failed to cancel transaction');
    }

    return body;
  } catch (error) {
    console.error('Error cancelling transaction:', error);
    throw error;
  }
}

export async function createTaxCalculation(
  stripeCustomerId: string,
  lineItems: { description: string; amount: number; quantity: number }[]
): Promise<Stripe.Tax.Calculation> {
  try {
    const response = await fetch('/api/stripe/calculateTax', {
      method: 'POST',
      body: JSON.stringify({ stripeCustomerId, lineItems })
    });

    const { body } = await response.json();

    if (response.status !== HTTP.OK) {
      if (body?.raw?.message) {
        throw Error(body.raw.message, { cause: 'Stripe' });
      }
    }

    return body;
  } catch (error) {
    console.error('[createTaxCalculation()]', error);
    throw error;
  }
}

export async function restoreBalanceAndDiscounts(
  stripeCustomerId: string,
  removedDiscounts?: Stripe.Discount,
  removedCredit?: boolean,
  customerCurrentBalance?: number
): Promise<Stripe.Invoice> {
  try {
    const response = await fetch('/api/stripe/invoices/restore', {
      method: 'POST',
      body: JSON.stringify({
        stripeCustomerId,
        removedDiscounts,
        removedCredit,
        customerCurrentBalance
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    return data.invoice;
  } catch (error) {
    console.error('[restoreBalanceAndDiscounts()]', error);
    throw error;
  }
}
